import { ArrowsClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowsClockwise";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { DocumentDropPane } from "./document-drop-pane";
import { DocumentList } from "./document-list";
import {
  UPLOAD_ITEM_STATE,
  type OnboardingDocument,
  type OnboardingDocumentType,
} from "./types";
import { UploadConflictModal } from "./upload-conflict-modal";
import { UploadProgress } from "./upload-progress";
import {
  MAX_FILES_PER_BATCH,
  useDocumentBatchUpload,
} from "./use-document-batch-upload";

const MAX_UPLOAD_SIZE = 20 * 1024 * 1024;

interface DocumentSectionProps {
  businessId: string;
  type: OnboardingDocumentType;
  documents: OnboardingDocument[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

// The uploader and everything sent so far, as one list. Files go up the moment they are
// chosen: the page header already said what to send, so a second click on a button that
// spends most of its life disabled adds nothing.
export const DocumentSection = ({
  businessId,
  type,
  documents,
  isLoading,
  isError,
}: DocumentSectionProps) => {
  const batch = useDocumentBatchUpload(businessId, type);
  const canRetry =
    !batch.isUploading &&
    batch.items.some(
      ({ status }) =>
        status.state === UPLOAD_ITEM_STATE.failed ||
        status.state === UPLOAD_ITEM_STATE.skipped,
    );
  const isEmpty =
    !isLoading && !isError && !documents?.length && !batch.items.length;
  const hasContents =
    batch.items.length > 0 ||
    !!documents?.length ||
    isLoading ||
    isError ||
    isEmpty;

  return (
    <>
      <DocumentDropPane
        maxFiles={MAX_FILES_PER_BATCH}
        maxSize={MAX_UPLOAD_SIZE}
        disabled={batch.isUploading}
        onFiles={batch.uploadAll}
        hasContents={hasContents}
      >
        {isEmpty && (
          <Empty className={emptyStateVariants({ layout: "inline" })}>
            <EmptyHeader>
              <EmptyDescription>No documents yet.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
        {isLoading && (
          <Stack gap={4} align="center" className="w-full py-4">
            <Spinner layout="centered" aria-label="Loading documents" />
          </Stack>
        )}
        {isError && (
          <Stack align="start" gap={4} className="w-full py-3">
            <Alert color="danger">
              We could not load your documents. Refresh the page to try again.
            </Alert>
          </Stack>
        )}
        {batch.items.length > 0 && <UploadProgress items={batch.items} />}
        {canRetry && (
          <Stack
            align="start"
            gap={4}
            direction="row"
            justify="end"
            className="w-full py-1"
          >
            <Button variant="link" onClick={batch.retry}>
              <ArrowsClockwiseIcon data-icon="inline-start" />
              Try again
            </Button>
          </Stack>
        )}
        {documents && documents.length > 0 && (
          <DocumentList businessId={businessId} documents={documents} />
        )}
      </DocumentDropPane>

      <UploadConflictModal
        conflict={batch.conflict}
        onCancel={() => batch.answerConflict(null)}
        onResolve={(resolution) => batch.answerConflict(resolution)}
      />
    </>
  );
};
